/**
 * 全局配置与运行时状态。
 * 供 GUI、检测器、渲染器共同读取。
 */
import * as posedetection from '@tensorflow-models/pose-detection';

/** 骨骼连线线宽（像素） */
export const DEFAULT_LINE_WIDTH = 2;
/** 关键点圆点半径（像素） */
export const DEFAULT_RADIUS = 4;

/** 摄像头分辨率选项（GUI 下拉用） */
export const VIDEO_SIZE = {
  '640 X 480': {width: 640, height: 480},
  '640 X 360': {width: 640, height: 360},
  '360 X 270': {width: 360, height: 270}
};

/**
 * 运行时状态。GUI 修改后会设置对应的 is*Changed 标志，
 * 主循环 checkGuiUpdate() 据此重新初始化相关模块。
 */
export const STATE = {
  camera: {targetFPS: 60, sizeOption: '640 X 480'},
  backend: '',
  flags: {},
  modelConfig: {},
  background: {},
  display: {}
};

/** 画面显示选项 */
export const DISPLAY_CONFIG = {
  showSkeleton: true,
  showBoundingBox: false,
  /** enableTracking 开启时，是否按人分配不同颜色的骨骼线与边界框 */
  colorByPerson: true,
  /** 是否在画面上显示追踪 ID */
  showTrackId: true,
};

/** 边界框相对关键点的内边距（像素） */
export const BBOX_PADDING = 8;

/** Canvas 背景默认配置（图片/视频由本地 File 提供，不再写死路径） */
export const BACKGROUND_CONFIG = {
  type: 'camera',
};

/** MoveNet 模型默认参数 */
export const MOVENET_CONFIG = {
  maxPoses: 6,
  type: 'lightning',
  scoreThreshold: 0.3,
  customModel: '/model/model.json',
  enableTracking: false
};

/**
 * 贴图内骨骼序号 → 人体关键点序号（含虚拟点 17、18）。
 * 图内序号: 0  1  2  3  4  5  6  7  8  9 10 11 12 13 14
 * 关键点:   0 17  6  8 10  5  7  9 18 12 14 16 11 13 15
 */
export const PIYING_KEYPOINT_MAP = [
  0, 17, 6, 8, 10, 5, 7, 9, 18, 12, 14, 16, 11, 13, 15,
];

/**
 * 应用运行门控。
 * running=false 时不进行姿态检测；换资源须先停止。
 */
export const RUNTIME = {
  running: false,
};

/** 是否允许开始运行：必须已选皮影；背景 camera 无需选文件 */
export function canStartRuntime(piyingReady, backgroundReady) {
  return !!piyingReady && !!backgroundReady;
}
/**
 * TF.js 可调环境变量及其合法取值范围。
 * 仅列出可在 GUI 中安全调节的 flag。
 */
export const TUNABLE_FLAG_VALUE_RANGE_MAP = {
  WEBGL_VERSION: [1, 2],
  WEBGL_CPU_FORWARD: [true, false],
  WEBGL_PACK: [true, false],
  WEBGL_FORCE_F16_TEXTURES: [true, false],
  WEBGL_RENDER_FLOAT32_CAPABLE: [true, false],
  WEBGL_FLUSH_THRESHOLD: [-1, 0, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
};

/** 各后端对应的可调 flag 列表（当前仅 WebGL） */
export const BACKEND_FLAGS_MAP = {
  ['tfjs-webgl']: [
    'WEBGL_VERSION', 'WEBGL_CPU_FORWARD', 'WEBGL_PACK',
    'WEBGL_FORCE_F16_TEXTURES', 'WEBGL_RENDER_FLOAT32_CAPABLE',
    'WEBGL_FLUSH_THRESHOLD'
  ]
};

/** 模型与推理后端的映射关系 */
export const MODEL_BACKEND_MAP = {
  [posedetection.SupportedModels.MoveNet]: ['tfjs-webgl']
};

/**
 * WebGL 环境变量在 GUI 中的中文显示名。
 *
 * WEBGL_VERSION          - WebGL 版本（1 或 2）
 * WEBGL_CPU_FORWARD      - 是否在 CPU 上执行部分算子
 * WEBGL_PACK             - 是否打包纹理以提升性能
 * WEBGL_FORCE_F16_TEXTURES - 是否强制使用 float16 纹理（降低精度、节省显存）
 * WEBGL_RENDER_FLOAT32_CAPABLE - 是否启用 float32 渲染能力
 * WEBGL_FLUSH_THRESHOLD  - GL 命令刷新等待时间（ms），影响 GPU 同步策略
 */
export const TUNABLE_FLAG_NAME_MAP = {
  WEBGL_VERSION: 'WebGL 版本',
  WEBGL_CPU_FORWARD: 'CPU 转发',
  WEBGL_PACK: '纹理打包',
  WEBGL_FORCE_F16_TEXTURES: '强制 float16 纹理',
  WEBGL_RENDER_FLOAT32_CAPABLE: '启用 float32 渲染',
  WEBGL_FLUSH_THRESHOLD: 'GL 刷新等待(ms)'
};
